import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useParams } from 'react-router-dom';
import { API } from '../../context/AuthContext';
import CSOCard from '../../components/shared/CSOCard';
import {
  Search,
  Filter,
  X,
  MapPin,
  Map,
  Layers,
  Building2,
  CheckCircle2,
  Calendar,
  Globe,
  Phone,
  Mail,
  ExternalLink,
  Download,
  FileText,
  Image,
  Users,
  UserCircle,
  Briefcase,
  Flag,
  Eye,
  BookOpen,
  Target,
  Award,
  Heart,
  Clock,
  DollarSign,
  Hash,
  Shield,
  Share2,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  LayoutGrid,
  List,
  Loader2,
  Send,
  FolderOpen,
  Play,
  Handshake,
  Newspaper,
  UserCheck
} from 'lucide-react';

// ── CSOsPage ──────────────────────────────────────────────────────────────────
export function CSOsPage() {
  const [searchParams] = useSearchParams();
  const [csos, setCsos] = useState([]);
  const [pagination, setPagination] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [thematics, setThematics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    province_id: '', district_id: '',
    thematic_area_id: '', cluster: '',
    org_type: '',   // <-- added
    zan_certified: '', women_led: '', youth_led: '',
    sort: 'name', page: 1, limit: 12,
  });

  useEffect(() => {
    Promise.all([API.get('/lookup/provinces'), API.get('/lookup/thematic')])
      .then(([p,t]) => { setProvinces(p.data); setThematics(t.data); });
  }, []);

  useEffect(() => {
    if (filters.province_id) API.get(`/lookup/districts?province_id=${filters.province_id}`).then(r=>setDistricts(r.data));
    else setDistricts([]);
  }, [filters.province_id]);

  const fetch = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)));
    API.get(`/csos?${p}`).then(r=>{setCsos(r.data.data);setPagination(r.data.pagination);}).catch(console.error).finally(()=>setLoading(false));
  }, [filters]);

  useEffect(()=>{ fetch(); },[fetch]);

  const set = (k,v) => setFilters(f=>({...f,page:1,[k]:v,...(k==='province_id'?{district_id:''}:{})}));

  return (
    <div style={{ background:'var(--gray-50)', minHeight:'80vh' }}>
      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'40px 0 32px', color:'white' }}>
        <div className="page-container">
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', marginBottom:8 }}>CSO Directory</h1>
          <p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.9rem' }}>Browse and discover Civil Society Organisations across Zimbabwe</p>
          <div style={{ marginTop:20, display:'flex', gap:10, maxWidth:560, alignItems:'center' }}>
            <div className="input-group" style={{ flex:1 }}>
              <span className="input-icon"><Search size={18} style={{ color:'var(--gray-400)' }} /></span>
              <input className="form-control" placeholder="Search organisations..."
                value={filters.search} onChange={e=>set('search',e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&fetch()}
                style={{ borderColor:'transparent' }}/>
            </div>
            <button onClick={fetch} className="btn btn-gold" style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Search size={16} /> Search
            </button>
          </div>
        </div>
      </div>

      <div className="page-container" style={{ paddingTop:28, paddingBottom:60 }}>
        <div className="csos-layout" style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:24 }}>
          {/* Sidebar filters */}
          <aside>
            <div className="card card-pad" style={{ position:'sticky', top:80 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
                <span style={{ fontWeight:700, fontSize:'0.88rem', display:'flex', alignItems:'center', gap:6 }}>
                  <Filter size={16} /> Filters
                </span>
                <button onClick={()=>setFilters(f=>({...f,province_id:'',district_id:'',thematic_area_id:'',cluster:'',org_type:'',zan_certified:'',women_led:'',youth_led:''}))}
                  style={{ background:'none',border:'none',color:'var(--crimson)',fontSize:'0.75rem',fontWeight:600,cursor:'pointer' }}>
                  Clear all
                </button>
              </div>
              {[
                { label:'Province', key:'province_id', opts:provinces.map(p=>({v:p.id,l:p.name})) },
                ...(districts.length?[{ label:'District', key:'district_id', opts:districts.map(d=>({v:d.id,l:d.name})) }]:[]),
                { label:'Thematic Area', key:'thematic_area_id', opts:thematics.map(t=>({v:t.id,l:t.name})) },
                { label:'Organisation Type', key:'org_type', opts:[
                  {v:'NGO', l:'NGO'},
                  {v:'CBO', l:'CBO'},
                  {v:'Trust', l:'Trust'},
                  {v:'PVO', l:'PVO'},
                  {v:'Network / Membership Organisation', l:'Network / Membership Organisation'},
                  {v:'FBO', l:'FBO'},
                  {v:'Government-Affiliated', l:'Government-Affiliated'},
                  {v:'International NGO', l:'International NGO'},
                  {v:'Business', l:'Business'}
                ]},
              ].map(f=>(
                <div key={f.key} style={{ marginBottom:14 }}>
                  <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:'var(--gray-500)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:6 }}>{f.label}</label>
                  <select className="form-control" value={filters[f.key]} onChange={e=>set(f.key,e.target.value)}>
                    <option value="">All</option>
                    {f.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ borderTop:'1px solid var(--gray-200)', paddingTop:14 }}>
                {[
                  { key:'zan_certified', label:'ZAN Certified Only' },
                  { key:'women_led', label:'Women-Led' },
                  { key:'youth_led', label:'Youth-Led' },
                ].map(cb=>(
                  <label key={cb.key} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, cursor:'pointer', fontSize:'0.82rem', fontWeight:500 }}>
                    <input type="checkbox" checked={filters[cb.key]==='true'} onChange={e=>set(cb.key,e.target.checked?'true':'')} style={{ accentColor:'var(--green)' }}/>
                    {cb.label}
                  </label>
                ))}
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
              <span style={{ fontSize:'0.85rem', color:'var(--gray-500)' }}>
                {loading ? <Loader2 size={16} className="spin" style={{ marginRight:8 }} /> : null}
                {loading ? 'Loading...' : `${pagination.total||0} organisations found`}
              </span>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <select className="form-control" style={{ width:140 }} value={filters.sort} onChange={e=>set('sort',e.target.value)}>
                  <option value="name">Name A–Z</option>
                  <option value="newest">Newest First</option>
                  <option value="views">Most Viewed</option>
                </select>
                <div style={{ display:'flex', border:'1px solid var(--gray-200)', borderRadius:6, overflow:'hidden' }}>
                  {[
                    [LayoutGrid, 'grid'],
                    [List, 'list']
                  ].map(([IconComponent, mode]) => (
                    <button key={mode} onClick={()=>setViewMode(mode)}
                      style={{ padding:'6px 10px', border:'none', background:viewMode===mode?'var(--green)':'white', color:viewMode===mode?'white':'var(--gray-500)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <IconComponent size={16} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div>
            : csos.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><Search size={48} /></div>
                <h3>No organisations found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
                {csos.map(c=><CSOCard key={c.id} cso={c}/>)}
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {csos.map(c=>(
                  <Link key={c.id} to={`/csos/${c.slug}`}
                    style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 18px', background:'white', borderRadius:10, border:'1px solid var(--gray-200)', textDecoration:'none', transition:'all 0.2s' }}
                    onMouseOver={e=>e.currentTarget.style.borderColor='var(--green-200)'}
                    onMouseOut={e=>e.currentTarget.style.borderColor='var(--gray-200)'}>
                    <div style={{ width:44, height:44, borderRadius:8, background:'var(--green-50)', border:'1px solid var(--green-100)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--green)', fontSize:'0.82rem', flexShrink:0, overflow:'hidden' }}>
                      {c.logo_url
                        ? <img src={c.logo_url.startsWith('/') ? `${c.logo_url}` : c.logo_url} alt={c.org_name} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                        : (c.acronym||c.org_name||'').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--gray-800)' }}>{c.org_name}</div>
                      <div style={{ fontSize:'0.78rem', color:'var(--gray-400)' }}>{c.province}{c.district?` / ${c.district}`:''} {c.reg_number?`· ${c.reg_number}`:''}</div>
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      {c.zan_certified && <span className="zan-badge"><CheckCircle2 size={14} /></span>}
                      <span className="btn btn-sm btn-secondary" style={{ display:'flex', alignItems:'center', gap:4 }}>View <ArrowRight size={14} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:28 }}>
                <button className="pg-btn" onClick={()=>set('page',filters.page-1)} disabled={filters.page<=1}>
                  <ChevronLeft size={16} />
                </button>
                {Array.from({length:Math.min(pagination.pages,7)},(_,i)=>i+1).map(p=>(
                  <button key={p} className={`pg-btn${filters.page===p?' active':''}`} onClick={()=>set('page',p)}>{p}</button>
                ))}
                <button className="pg-btn" onClick={()=>set('page',filters.page+1)} disabled={filters.page>=pagination.pages}>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// ── CSOPublicProfile ──────────────────────────────────────────────────────────
export function CSOPublicProfile() {
  const { slug } = useParams();
  const [cso, setCso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('about');
  const [lightbox, setLightbox] = useState(null);

  useEffect(()=>{
    API.get(`/csos/${slug}`).then(r=>setCso(r.data)).catch(console.error).finally(()=>setLoading(false));
  },[slug]);

  if (loading) return <div className="loading-center"><Loader2 size={32} className="spin" /></div>;
  if (!cso) return <div style={{ textAlign:'center', padding:'80px 20px' }}><h2>Organisation not found</h2><Link to="/csos" className="btn btn-primary" style={{ marginTop:16, display:'inline-flex', alignItems:'center', gap:6 }}><ArrowRight size={16} style={{ transform:'rotate(180deg)' }} /> Back to Directory</Link></div>;

  const photos = cso.media?.filter(m=>m.media_type==='photo')||[];
  const docs = cso.media?.filter(m=>m.media_type==='document')||[];

  const cardHover = {
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  };

  const onMouseOver = (e) => {
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
    e.currentTarget.style.borderColor = 'var(--green-200)';
  };
  const onMouseOut = (e) => {
    e.currentTarget.style.transform = 'none';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--gray-200)';
  };

  return (
    <div>
      {/* Banner */}
      <div style={{ height:200, background:'linear-gradient(135deg,var(--green-dark),var(--green))', position:'relative', overflow:'hidden' }}>
        {cso.banner_url && <img src={`${cso.banner_url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>}
      </div>

      {/* Profile header */}
      <div style={{ background:'white', borderBottom:'1px solid var(--gray-200)' }}>
        <div className="page-container" style={{ paddingTop:16, paddingBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:16 }}>
            <div style={{ display:'flex', alignItems:'flex-end', gap:18 }}>
              <div style={{ 
                width:90, height:90, borderRadius:12, background:'white', border:'3px solid white', 
                boxShadow:'var(--shadow)', marginTop:-45, overflow:'hidden', 
                display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, 
                color:'var(--green)', fontSize:'1.4rem' 
              }}>
                {cso.logo_url 
                  ? <img src={cso.logo_url.startsWith('/') ? `${cso.logo_url}` : cso.logo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/> 
                  : (cso.acronym ? cso.acronym.toUpperCase() : (cso.org_name||'').slice(0,2).toUpperCase())
                }
              </div>
              <div style={{ paddingBottom:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <h1 style={{ fontSize:'1.4rem', fontWeight:800 }}>{cso.org_name}</h1>
                  {cso.zan_certified && <span style={{ background:'#e8f5e9', color:'#2e7d32', padding:'3px 10px', borderRadius:20, fontSize:'0.78rem', fontWeight:700, border:'1px solid #c8e6c9', display:'flex', alignItems:'center', gap:4 }}><CheckCircle2 size={14} /> Verified</span>}
                </div>
                {cso.org_type && <div style={{ fontSize:'0.83rem', color:'var(--gray-500)' }}>{cso.org_type}</div>}
                <div style={{ display:'flex', gap:14, fontSize:'0.78rem', color:'var(--gray-500)', marginTop:6, flexWrap:'wrap' }}>
                  {cso.year_established && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Calendar size={12} /> Est. {cso.year_established}</span>}
                  {cso.province && <span style={{ display:'flex', alignItems:'center', gap:4 }}><MapPin size={12} /> {cso.province}</span>}
                  {cso.reg_number && <span style={{ display:'flex', alignItems:'center', gap:4 }}><Hash size={12} /> {cso.reg_number}</span>}
                </div>
              </div>
            </div>
            {/* Social media buttons - enhanced with fallback */}
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {cso.website && <a href={cso.website} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', gap:4 }}><Globe size={14} /> Website</a>}
              {cso.facebook && (
                <a href={cso.facebook} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', justifyContent:'center' }} title="Facebook">
                  <img src={`${process.env.PUBLIC_URL || ''}/icons/social/facebook.png`} alt="Facebook" style={{ width:16, height:16 }} onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:14px;font-weight:bold;">f</span>'; }} />
                </a>
              )}
              {cso.instagram && (
                <a href={cso.instagram} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', justifyContent:'center' }} title="Instagram">
                  <img src={`${process.env.PUBLIC_URL || ''}/icons/social/instagram.png`} alt="Instagram" style={{ width:16, height:16 }} onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:14px;font-weight:bold;">IG</span>'; }} />
                </a>
              )}
              {cso.x_twitter && (
                <a href={cso.x_twitter} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', justifyContent:'center' }} title="X (Twitter)">
                  <img src={`${process.env.PUBLIC_URL || ''}/icons/social/twitter.png`} alt="X (Twitter)" style={{ width:16, height:16 }} onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:14px;font-weight:bold;">𝕏</span>'; }} />
                </a>
              )}
              {cso.linkedin && (
                <a href={cso.linkedin} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', justifyContent:'center' }} title="LinkedIn">
                  <img src={`${process.env.PUBLIC_URL || ''}/icons/social/linkedin.svg`} alt="LinkedIn" style={{ width:16, height:16 }} onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:14px;font-weight:bold;">in</span>'; }} />
                </a>
              )}
              {cso.youtube && (
                <a href={cso.youtube} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', justifyContent:'center' }} title="YouTube">
                  <img src={`${process.env.PUBLIC_URL || ''}/icons/social/youtube.svg`} alt="YouTube" style={{ width:16, height:16 }} onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:14px;font-weight:bold;">▶</span>'; }} />
                </a>
              )}
              {cso.whatsapp && (
                <a href={cso.whatsapp} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', justifyContent:'center' }} title="WhatsApp">
                  <img src={`${process.env.PUBLIC_URL || ''}/icons/social/whatsapp.svg`} alt="WhatsApp" style={{ width:16, height:16 }} onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement.innerHTML = '<span style="font-size:14px;font-weight:bold;">WA</span>'; }} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + content */}
      <div className="page-container" style={{ paddingTop:24, paddingBottom:60 }}>
        <div className="tab-list">
          {[
            ['about', BookOpen, 'Overview'],
            ['programs', Target, 'Programs'],
            ['gallery', Image, `Gallery (${photos.length})`],
            ['contact', Phone, 'Contact'],
            ['board', Users, 'Leadership']
          ].map(([k, IconComponent, l])=>(
            <button key={k} className={`tab-btn${tab===k?' active':''}`} onClick={()=>setTab(k)}>
              <IconComponent size={16} style={{ marginRight:6 }} /> {l}
            </button>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:24, alignItems:'start' }}>
          <div>
            {tab==='about' && (
              <div className="anim-fadeIn">
                {cso.mission && (
                  <div className="card card-pad" style={{ marginBottom:16, ...cardHover }}
                       onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                    <h3 style={{ color:'var(--green)', fontSize:'0.9rem', fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}><Flag size={18} /> Mission</h3>
                    <p style={{ fontSize:'0.88rem', color:'var(--gray-600)', lineHeight:1.7 }}>{cso.mission}</p>
                  </div>
                )}
                {cso.vision && (
                  <div className="card card-pad" style={{ marginBottom:16, ...cardHover }}
                       onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                    <h3 style={{ color:'var(--green)', fontSize:'0.9rem', fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}><Eye size={18} /> Vision</h3>
                    <p style={{ fontSize:'0.88rem', color:'var(--gray-600)', lineHeight:1.7 }}>{cso.vision}</p>
                  </div>
                )}
                {cso.background && (
                  <div className="card card-pad" style={{ marginBottom:16, ...cardHover }}
                       onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                    <h3 style={{ color:'var(--green)', fontSize:'0.9rem', fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8 }}><BookOpen size={18} /> Background</h3>
                    <p style={{ fontSize:'0.88rem', color:'var(--gray-600)', lineHeight:1.7 }}>{cso.background}</p>
                  </div>
                )}
                {cso.thematic_areas?.length>0 && (
                  <div className="card card-pad" style={cardHover}
                       onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                    <h3 style={{ color:'var(--green)', fontSize:'0.9rem', fontWeight:700, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}><Layers size={18} /> Thematic Areas</h3>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>{cso.thematic_areas.map((t,i)=><span key={i} className="tag">{t}</span>)}</div>
                  </div>
                )}
              </div>
            )}
            {tab==='programs' && (
              <div className="anim-fadeIn">
                {!cso.programs?.length ? <div className="empty-state"><div className="empty-state-icon"><Target size={48} /></div><div>No programs listed</div></div>
                : cso.programs.map(p=>(
                  <div key={p.id} className="card card-pad" style={{ marginBottom:12, borderLeft:`4px solid ${p.status==='active'?'var(--green)':'var(--gray-300)'}`, ...cardHover }}
                       onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                    <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                          <Target size={18} style={{ color:'var(--green)' }} />
                          <strong style={{ fontSize:'0.9rem' }}>{p.program_name}</strong>
                        </div>
                        {p.description && <p style={{ fontSize:'0.82rem', color:'var(--gray-600)', lineHeight:1.6 }}>{p.description}</p>}
                        {p.beneficiaries && <div style={{ fontSize:'0.78rem', color:'var(--gray-400)', marginTop:6, display:'flex', alignItems:'center', gap:4 }}><Users size={14} /> {p.beneficiaries}</div>}
                      </div>
                      <span className={`badge ${p.status==='active'?'badge-green':'badge-gray'}`}>{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab==='gallery' && (
              <div className="anim-fadeIn">
                {!photos.length ? <div className="empty-state"><div className="empty-state-icon"><Image size={48} /></div><div>No photos</div></div>
                : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
                    {photos.map(p=>(
                      <div key={p.id} onClick={()=>setLightbox(p)} style={{ borderRadius:8, overflow:'hidden', aspectRatio:'4/3', cursor:'pointer', background:'var(--gray-100)', transition:'transform 0.2s' }}
                           onMouseOver={e=>e.currentTarget.style.transform='scale(1.03)'}
                           onMouseOut={e=>e.currentTarget.style.transform='scale(1)'}>
                        <img src={`${p.file_url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      </div>
                    ))}
                  </div>}
                {docs.length>0 && <div style={{ marginTop:24 }}><h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}><FileText size={18} /> Documents</h3>{docs.map(d=>(
                  <a key={d.id} href={`${d.file_url}`} target="_blank" rel="noreferrer"
                    className="card" style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:12, marginBottom:8, textDecoration:'none', color:'var(--gray-800)', ...cardHover }}
                    onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                    <FileText size={20} style={{ color:'var(--green)' }} />
                    <span style={{ flex:1, fontSize:'0.85rem', fontWeight:500 }}>{d.title||d.file_url.split('/').pop()}</span>
                    <Download size={16} style={{ color:'var(--green)' }} />
                  </a>
                ))}</div>}
              </div>
            )}
            {tab==='contact' && (
              <div className="anim-fadeIn grid-2">
                <div className="card card-pad" style={cardHover} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                  <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:14 }}>Organisation Contact</h3>
                  {[
                    [Phone, 'Phone', cso.phone],
                    [Mail, 'Email', cso.email],
                    [Globe, 'Website', cso.website],
                    [MapPin, 'Address', cso.physical_address],
                    [Map, 'Province', cso.province],
                    [MapPin, 'District', cso.district]
                  ].filter(([,,v])=>v).map(([IconComponent,l,v])=>(
                    <div key={l} style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'0.82rem' }}>
                      <IconComponent size={16} style={{ color:'var(--green)', flexShrink:0, marginTop:2 }} />
                      <div>
                        <div style={{ color:'var(--gray-400)', fontSize:'0.7rem', fontWeight:600, textTransform:'uppercase' }}>{l}</div>
                        <div style={{ color:'var(--gray-700)', marginTop:1 }}>{v}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="card card-pad" style={cardHover} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                  <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:14 }}>Contact Person</h3>
                  {[
                    [UserCircle, 'Name', cso.contact_name],
                    [Briefcase, 'Designation', cso.contact_designation],
                    [Phone, 'Phone', cso.contact_phone],
                    [Mail, 'Email', cso.contact_email]
                  ].filter(([,,v])=>v).map(([IconComponent,l,v])=>(
                    <div key={l} style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'0.82rem' }}>
                      <IconComponent size={16} style={{ color:'var(--green)', flexShrink:0, marginTop:2 }} />
                      <div>
                        <div style={{ color:'var(--gray-400)', fontSize:'0.7rem', fontWeight:600, textTransform:'uppercase' }}>{l}</div>
                        <div style={{ color:'var(--gray-700)', marginTop:1 }}>{v}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab==='board' && (
              <div className="anim-fadeIn">
                {!cso.directors?.length ? <div className="empty-state"><div className="empty-state-icon"><Users size={48} /></div><div>No director details</div></div>
                : <div className="grid-3">{cso.directors.map((d,i)=>(
                    <div key={i} className="card card-pad" style={{ textAlign:'center', ...cardHover }}
                         onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                      <div style={{ width:48, height:48, borderRadius:'50%', background:'var(--green)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:'1rem', margin:'0 auto 10px' }}>{(d.full_name||'?').charAt(0)}</div>
                      <div style={{ fontWeight:700, fontSize:'0.88rem' }}>{d.full_name}</div>
                      <div style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>{d.role}</div>
                      {d.phone && <div style={{ fontSize:'0.78rem', color:'var(--gray-500)', marginTop:4, display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}><Phone size={12} /> {d.phone}</div>}
                    </div>
                  ))}</div>}
              </div>
            )}
          </div>

          {/* Sidebar – Quick Info WITHOUT Budget or Staff Strength */}
          <aside>
            <div className="card card-pad" style={{ marginBottom:14, ...cardHover }} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
              <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:12 }}>Quick Info</h4>
              {[
                [Hash, 'Reg No.', cso.reg_number],
                [FileText, 'Legal Status', cso.legal_status],
                [Calendar, 'Est.', cso.year_established],
                // [Users, 'Staff', cso.staff_strength],       // removed
                // [DollarSign, 'Budget', cso.annual_budget], // removed
              ].filter(([,,v])=>v).map(([IconComponent,l,v])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'0.8rem' }}>
                  <span style={{ color:'var(--gray-500)', display:'flex', alignItems:'center', gap:4 }}>
                    <IconComponent size={14} /> {l}
                  </span>
                  <strong style={{ color:'var(--gray-700)' }}>{v}</strong>
                </div>
              ))}
            </div>
            {(cso.facebook_followers||cso.x_followers) && (
              <div className="card card-pad" style={{ marginBottom:14, ...cardHover }} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
                <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:10 }}>Social Media</h4>
                {[
                  ['Facebook', cso.facebook_followers, 'Followers'],
                  ['X (Twitter)', cso.x_followers, 'Followers'],
                  ['LinkedIn', cso.linkedin_followers, 'Followers'],
                  ['YouTube', cso.youtube_subscribers, 'Subscribers']
                ].filter(([,v])=>v).map(([p,v,l])=>(
                  <div key={p} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'0.8rem' }}>
                    <span style={{ color:'var(--gray-500)', display:'flex', alignItems:'center', gap:4 }}>
                      <Share2 size={14} /> {p}
                    </span>
                    <strong>{parseInt(v).toLocaleString()} {l}</strong>
                  </div>
                ))}
              </div>
            )}
            <div className="card card-pad" style={cardHover} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
              <h4 style={{ fontSize:'0.85rem', fontWeight:700, marginBottom:10 }}>Memberships</h4>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {cso.member_zan && <span className="badge badge-green"><Shield size={12} style={{ marginRight:4 }} /> ZAN</span>}
                {cso.member_nac && <span className="badge badge-green">NAC</span>}
                {cso.member_nango && <span className="badge badge-green">NANGO</span>}
                {cso.member_mopslsw && <span className="badge badge-green">MoPSLSW</span>}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {lightbox && (
        <div onClick={()=>setLightbox(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <img src={`${lightbox.file_url}`} alt="" style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:10, objectFit:'contain' }}/>
          <button onClick={()=>setLightbox(null)} style={{ position:'absolute', top:20, right:20, background:'rgba(255,255,255,0.15)', border:'none', color:'white', width:40, height:40, borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <X size={24} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── AboutPage ─────────────────────────────────────────────────────────────────
export function AboutPage() {
  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'56px 0', color:'white' }}>
        <div className="page-container">
          <div className="section-eyebrow">Who We Are</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2.4rem', marginBottom:12 }}>About Zim CSO Database</h1>
          <p style={{ color:'rgba(255,255,255,0.8)', maxWidth:560, lineHeight:1.7 }}>A national initiative by the Zimbabwe AIDS Network to enhance the visibility and coordination of Civil Society Organizations in Zimbabwe's health sector.</p>
        </div>
      </div>
      <div className="page-container section-pad">
        <div className="grid-2" style={{ gap:48, marginBottom:60, padding: '0 24px' }}>
          {/* Left column – ZAN info */}
          <div>
            <div className="section-eyebrow">Our Organisation</div>
            <h2 className="section-title">Zimbabwe AIDS Network (ZAN)</h2>
            <div className="gold-line"/>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              Zimbabwe AIDS Network was established in 1992 as a membership-based network with the fundamental responsibility to coordinate Civil Society Organizations (CSOs) in the country to respond to the HIV and AIDS pandemic as well as other non-communicable diseases and emerging health pandemics efficiently and effectively.
            </p>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              ZAN plays a pivotal role in coordinating CSO activities, avoiding duplication of effort, and ensuring maximum reach and impact for health programming.
            </p>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:24 }}>
              The Zim CSO Database is funded by the <strong>Global Fund</strong> and supported by <strong>UNDP</strong>.
            </p>

            {/* ZAN Core Values */}
            <div style={{
              background: 'var(--green-50)',
              borderLeft: '4px solid var(--green)',
              borderRadius: '8px',
              padding: '18px 20px',
              marginBottom: 20
            }}>
              <h3 style={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: 'var(--green)',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <Shield size={18} /> ZAN Core Values
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {['Accountability', 'Transparency', 'Representation', 'Integrity', 'Inclusivity', 'Equity'].map(value => (
                  <span key={value} style={{
                    background: 'white',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'var(--gray-700)',
                    border: '1px solid var(--green-100)'
                  }}>
                    {value}
                  </span>
                ))}
              </div>
            </div>

            {/* ZAN's Strategic Focus */}
            <div style={{
              background: 'var(--green-50)',
              borderLeft: '4px solid var(--green)',
              borderRadius: '8px',
              padding: '18px 20px',
              marginBottom: 20
            }}>
              <h3 style={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: 'var(--green)',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <Target size={18} /> ZAN’s Strategic Focus
              </h3>
              <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--gray-600)', fontSize: '0.85rem', lineHeight: 1.7 }}>
                <li><strong>Networking and Coordination</strong></li>
                <li><strong>Advocacy and Communication</strong></li>
                <li><strong>Research and Documentation</strong></li>
                <li><strong>Capacity Building</strong></li>
                <li><strong>Resource Mobilization</strong></li>
              </ol>
            </div>

            {/* Become a ZAN Member */}
            <div style={{
              background: 'var(--green-50)',
              borderLeft: '4px solid var(--gold)',
              borderRadius: '8px',
              padding: '18px 20px',
              marginBottom: 20
            }}>
              <h3 style={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: 'var(--green)',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <UserCheck size={18} /> Become a ZAN Member
              </h3>
              <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--gray-600)', fontSize: '0.85rem', lineHeight: 1.7 }}>
                <li>Networking & Collaboration Opportunities</li>
                <li>Capacity Building & Training</li>
                <li>Advocacy & Policy Influence</li>
                <li>Boost Your Visibility</li>
                <li>Strengthened Community Impact</li>
                <li>Credibility & Recognition</li>
              </ul>
            </div>
          </div>

          {/* Right column – About the Database (new content) */}
          <div>
            <div className="section-eyebrow">The Platform</div>
            <h2 className="section-title">About This Database</h2>
            <div className="gold-line"/>

            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              The Zim CSO Database is the definitive online hub of registered and validated community-based and civil society organisations (CSOs) responding to HIV & AIDS, Sexual and Reproductive Health (SRH), non-communicable diseases (NCDs), and general health in Zimbabwe. More than a simple listing, it is a dynamic coordination tool that answers the critical question: <em>Who is doing what, where, and with whom?</em>
            </p>

            <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--green)', marginBottom:6 }}>Background and Strategic Foundation</h3>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              This platform was developed with the support of UNDP under the Capacity Development programme, as a direct output of the Civil Society Organisations Strategic Action Plan. That plan achieved two foundational milestones: it defined the future role and mandate of civil society in supporting the achievement of the 90-90-90 (and subsequently 95-95-95) HIV targets, producing a Civil Society Charter to communicate this mandate to key stakeholders; and it disseminated an Engagement and Accountability Framework, guiding how civil society can drive more comprehensive health programming and integrate HIV within the broader health agenda to help achieve Universal Health Coverage.
            </p>

            <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--green)', marginBottom:6 }}>Powerful Search and Discovery</h3>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              The database’s structure allows you to identify and explore CSOs through multiple intuitive pathways:
            </p>
            <ul style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14, paddingLeft:20 }}>
              <li><strong>By Service Speciality:</strong> Filter organisations by the core services they offer, including Advocacy, Clinical Services, Health Promotion, Research, and Economic Empowerment & Livelihoods.</li>
              <li><strong>By Programme Keywords:</strong> Search using key terms aligned to HIV and SRHR programming, such as Key Populations, PLHIV, OVCs, MSM, Women and Gender, Youth, AGYW, LGBTIQ, and Drug Users.</li>
              <li><strong>By Geographic Location:</strong> Every profiled CSO has been mapped using precise geographic coordinates. An interactive map lets you browse from the provincial level right down to individual districts. Click on any province, and a list of all CSOs operating there will appear, complete with physical addresses viewable via satellite or standard maps.</li>
            </ul>

            <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--green)', marginBottom:6 }}>Integrated e-Directory</h3>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              The platform doubles as a comprehensive e-directory, providing verified contact details for each organisation, including official email addresses, telephone numbers, and the names of key contact persons. This transparency is vital, as an efficient and sustainable HIV, TB, and health response depends on an enabling legal and policy environment that protects the human rights of people living with and impacted by HIV and TB, as well as vulnerable and key populations.
            </p>

            <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--green)', marginBottom:6 }}>Resource Centre</h3>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              A dedicated resources section offers a curated library of key strategic documents available for download. These include the Zimbabwe National AIDS Strategic Plan, the Legal Environment Assessment, the National HIV Communication Strategy, and other regional and international frameworks on human rights, the Sustainable Development Goals, and global commitments to Universal Health Coverage. Practical guidance is also provided, including step-by-step information on how to register as a Private Voluntary Organisation (PVO) in Zimbabwe, to support those wishing to formally join the health civil society sector.
            </p>

            <h3 style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--green)', marginBottom:6 }}>Coordination Hub</h3>
            <p style={{ color:'var(--gray-600)', lineHeight:1.8, marginBottom:14 }}>
              At the heart of the database’s coordination function is a detailed profile of the <strong>Zimbabwe AIDS Network (ZAN)</strong>, the recognised civil society coordinating platform for HIV & AIDS, SRHR, and health. By bringing all this information together in one place, the ZimHealthCSOs Database strengthens partnerships, avoids duplication, and supports a truly coordinated national response.
            </p>

            {/* ZAN Certification box – kept from previous version */}
            <div style={{ background:'var(--green-50)', borderRadius:10, padding:18, border:'1px solid var(--green-100)', marginTop:14 }}>
              <h4 style={{ color:'var(--green)', marginBottom:8, fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
                <Award size={20} /> ZAN Certification
              </h4>
              <p style={{ fontSize:'0.85rem', color:'var(--gray-600)', lineHeight:1.6 }}>
                ZAN field officers physically visit and verify each CSO before awarding the ZAN Certification badge — signifying the organisation is active, verified, and meets ZAN's standards.
              </p>
            </div>
          </div>
        </div>

        {/* Funding & Support section – unchanged */}
        <div style={{ marginBottom:60, padding: '0 24px' }}>
          <h2 className="section-title" style={{ textAlign:'center' }}>Funding & Support</h2>
          <div className="gold-line" style={{ margin:'0 auto 36px' }}/>
          <div className="grid-3" style={{ maxWidth:700, margin:'0 auto' }}>
            {[
              { name:'Global Fund', role:'Primary Funder', icon: Globe },
              { name:'FACT Zimbabwe', role:'Sub-Recipient (SR)', icon: Handshake },
              { name:'UNDP', role:'Technical Support', icon: Building2 }
            ].map(p => {
              const FundIcon = p.icon;
              return (
                <div key={p.name} className="card card-pad" style={{ textAlign:'center' }}>
                  <FundIcon size={32} style={{ marginBottom:10, color:'var(--green)' }} />
                  <div style={{ fontWeight:700, color:'var(--green)' }}>{p.name}</div>
                  <div style={{ fontSize:'0.8rem', color:'var(--gray-500)', marginTop:4 }}>{p.role}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ResourcesPage ─────────────────────────────────────────────────────────────
export function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  useEffect(()=>{ API.get('/resources?limit=50').then(r=>setResources(r.data)).finally(()=>setLoading(false)); },[]);
  const filtered = category ? resources.filter(r=>r.category===category) : resources;
  const categories = [...new Set(resources.map(r=>r.category).filter(Boolean))];

  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'48px 0', color:'white' }}>
        <div className="page-container">
          <div className="section-eyebrow">Library</div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', marginBottom:8 }}>Resources</h1>
          <p style={{ color:'rgba(255,255,255,0.8)' }}>Policies, reports, guides, and training materials for CSOs in Zimbabwe</p>
        </div>
      </div>
      <div className="page-container section-pad">
        {categories.length>0 && (
          <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
            <button onClick={()=>setCategory('')} className={`btn btn-sm ${!category?'btn-primary':'btn-secondary'}`}>All</button>
            {categories.map(c=><button key={c} onClick={()=>setCategory(c)} className={`btn btn-sm ${category===c?'btn-primary':'btn-secondary'}`}>{c}</button>)}
          </div>
        )}
        {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div>
        : filtered.length === 0 ? <div className="empty-state"><div className="empty-state-icon"><FolderOpen size={48} /></div><h3>No resources yet</h3><p>Check back soon for reports, policies, and guides.</p></div>
        : <div className="grid-3">{filtered.map(r=>(
            <div key={r.id} className="card card-hover" style={{ overflow:'hidden' }}>
              <div style={{ padding:'20px 20px 14px', background:'var(--green-50)', borderBottom:'1px solid var(--green-100)', display:'flex', alignItems:'center', gap:12 }}>
                {r.resource_type?.toLowerCase().includes('pdf') ? <FileText size={28} style={{ color:'var(--green)' }} /> :
                 r.resource_type?.toLowerCase().includes('video') ? <Play size={28} style={{ color:'var(--green)' }} /> :
                 <FolderOpen size={28} style={{ color:'var(--green)' }} />}
                <div><span className="badge badge-green" style={{ fontSize:'0.68rem' }}>{r.category}</span></div>
              </div>
              <div style={{ padding:'14px 20px 16px' }}>
                <div style={{ fontWeight:700, fontSize:'0.88rem', marginBottom:6 }}>{r.title}</div>
                {r.description && <p style={{ fontSize:'0.78rem', color:'var(--gray-500)', lineHeight:1.5, marginBottom:12 }}>{r.description}</p>}
                <a href={r.external_url||r.file_url||'#'} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary" style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  {r.external_url ? <><ExternalLink size={14} /> View Resource</> : <><Download size={14} /> Download</>}
                </a>
              </div>
            </div>
          ))}</div>}
      </div>
    </div>
  );
}

// ── ContactPage ───────────────────────────────────────────────────────────────
export function ContactPage() {
  const [form, setForm] = useState({ name:'', email:'', subject:'', message:'' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSending(true);
    try { await API.post('/contact', form); setSent(true); setForm({ name:'', email:'', subject:'', message:'' }); }
    catch { alert('Failed to send. Please try again.'); }
    finally { setSending(false); }
  };

  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'48px 0', color:'white' }}>
        <div className="page-container">
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', marginBottom:8 }}>Contact Us</h1>
          <p style={{ color:'rgba(255,255,255,0.8)' }}>Get in touch with Zimbabwe AIDS Network</p>
        </div>
      </div>
      <div className="page-container section-pad">
        <div className="grid-2" style={{ gap:48, alignItems:'start', padding: '0 24px' }}>
          <div>
            <h2 className="section-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <Send size={24} /> Send Us a Message
            </h2>
            <div className="gold-line"/>
            {sent ? (
              <div className="alert alert-success" style={{ display:'flex', alignItems:'center', gap:8 }}>
                <CheckCircle2 size={20} /> Thank you! Your message has been received. We'll respond within 2-3 business days.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Your Name <span className="req">*</span></label><input className="form-control" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Email Address <span className="req">*</span></label><input type="email" className="form-control" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
                </div>
                <div className="form-group"><label className="form-label">Subject</label><input className="form-control" value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} placeholder="How can we help?"/></div>
                <div className="form-group"><label className="form-label">Message <span className="req">*</span></label><textarea className="form-control" required style={{ minHeight:140 }} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} placeholder="Tell us about your enquiry..."/></div>
                <button type="submit" disabled={sending} className="btn btn-primary btn-lg" style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {sending ? <><Loader2 size={18} className="spin" /> Sending...</> : <><Send size={18} /> Send Message</>}
                </button>
              </form>
            )}
          </div>
          <div>
            <h2 className="section-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <Phone size={24} /> Contact Information
            </h2>
            <div className="gold-line"/>
            {[
              [MapPin, 'Physical Address', '30 St Patricks Road, Hatfield, Harare, Zimbabwe'],
              [Phone, 'Phone', '+263 242 570 604 / +263 77 345 1238'],
              [Mail, 'Email', 'zimaidsnetworkdirector@gmail.com'],
              [Globe, 'Website', 'www.zimaidsnetwork.org']
            ].map(([IconComponent,l,v])=>(
              <div key={l} style={{ display:'flex', gap:14, padding:'16px 0', borderBottom:'1px solid var(--gray-100)' }}>
                <div style={{ width:40, height:40, background:'var(--green-50)', border:'1px solid var(--green-100)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'var(--green)' }}>
                  <IconComponent size={18} />
                </div>
                <div>
                  <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'1px' }}>{l}</div>
                  <div style={{ fontSize:'0.9rem', color:'var(--gray-700)', marginTop:2 }}>{v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── NewsPage ──────────────────────────────────────────────────────────────────
export function NewsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ API.get('/news?limit=20').then(r=>setPosts(r.data)).finally(()=>setLoading(false)); },[]);
  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'48px 0', color:'white' }}>
        <div className="page-container">
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', marginBottom:8 }}>News & Updates</h1>
          <p style={{ color:'rgba(255,255,255,0.8)' }}>Latest news from ZAN and Zimbabwe's civil society sector</p>
        </div>
      </div>
      <div className="page-container section-pad" style={{ paddingLeft: '40px', paddingRight: '40px' }}>
        {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div>
        : posts.length===0 ? <div className="empty-state"><div className="empty-state-icon"><Newspaper size={48} /></div><h3>No news yet</h3></div>
        : <div className="grid-3">{posts.map(p=>(
            <Link key={p.id} to={`/news/${p.slug}`}
              className="card card-hover" style={{ textDecoration:'none', overflow:'hidden', display:'flex', flexDirection:'column' }}>
              <div style={{ height:140, background:'var(--green-50)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {p.cover_image_url ? <img src={p.cover_image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <Newspaper size={40} style={{ color:'var(--green)' }} />}
              </div>
              <div style={{ padding:'16px 18px', flex:1 }}>
                {p.category && <span className="badge badge-green" style={{ fontSize:'0.68rem', marginBottom:8, display:'inline-flex' }}>{p.category}</span>}
                <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--gray-800)', lineHeight:1.4, marginBottom:8 }}>{p.title}</div>
                {p.summary && <p style={{ fontSize:'0.78rem', color:'var(--gray-500)', lineHeight:1.5 }}>{p.summary.slice(0,100)}...</p>}
              </div>
              <div style={{ padding:'10px 18px', borderTop:'1px solid var(--gray-100)', fontSize:'0.72rem', color:'var(--gray-400)', display:'flex', alignItems:'center', gap:4 }}>
                <Calendar size={12} /> {p.published_at?new Date(p.published_at).toLocaleDateString('en',{day:'numeric',month:'short',year:'numeric'}):''}
              </div>
            </Link>
          ))}</div>}
      </div>
    </div>
  );
}

// ── NewsArticle ───────────────────────────────────────────────────────────────
export function NewsArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ API.get(`/news/${slug}`).then(r=>setPost(r.data)).finally(()=>setLoading(false)); },[slug]);
  if (loading) return <div className="loading-center"><Loader2 size={32} className="spin" /></div>;
  if (!post) return <div style={{ textAlign:'center', padding:80 }}><h2>Article not found</h2><Link to="/news" className="btn btn-primary" style={{ marginTop:16, display:'inline-flex', alignItems:'center', gap:6 }}><ChevronLeft size={16} /> Back to News</Link></div>;
  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'48px 0', color:'white' }}>
        <div className="page-container" style={{ maxWidth:800 }}>
          <Link
            to="/news"
            style={{
              color:'rgba(255,255,255,0.6)',
              fontSize:'0.82rem',
              display:'flex',
              alignItems:'center',
              gap:4,
              marginBottom:24
            }}
          >
            <ChevronLeft size={16} /> Back to News
          </Link>
          {post.category && (
            <span className="badge badge-green" style={{ marginBottom:12, display:'inline-flex' }}>
              {post.category}
            </span>
          )}
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', lineHeight:1.3, marginBottom:12 }}>{post.title}</h1>
          {post.published_at && (
            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:4 }}>
              <Calendar size={14} /> {new Date(post.published_at).toLocaleDateString('en',{day:'numeric',month:'long',year:'numeric'})}
            </div>
          )}
        </div>
      </div>
      <div className="page-container section-pad" style={{ maxWidth:800, paddingLeft: '40px', paddingRight: '40px' }}>
        {post.summary && <p style={{ fontSize:'1.05rem', color:'var(--gray-600)', lineHeight:1.7, marginBottom:24, fontStyle:'italic' }}>{post.summary}</p>}
        {post.content && <div style={{ fontSize:'0.92rem', color:'var(--gray-700)', lineHeight:1.9 }}>{post.content.split('\n').filter(Boolean).map((p,i)=><p key={i} style={{ marginBottom:14 }}>{p}</p>)}</div>}
      </div>
    </div>
  );
}

// ── ProvincesPage ─────────────────────────────────────────────────────────────
export function ProvincesPage() {
  const [provinces, setProvinces] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(()=>{
    API.get('/lookup/provinces').then(r=>setProvinces(r.data));
    API.get('/stats').then(r=>setStats(r.data));
  },[]);
  const provStats = Object.fromEntries((stats?.by_province||[]).map(p=>[p.province,parseInt(p.count)]));

  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,var(--green-dark),var(--green))', padding:'48px 0', color:'white' }}>
        <div className="page-container">
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:'2rem', marginBottom:8, display:'flex', alignItems:'center', gap:8 }}>
            <Map size={28} /> Provinces
          </h1>
          <p style={{ color:'rgba(255,255,255,0.8)' }}>Browse CSOs by Zimbabwe's 10 provinces</p>
        </div>
      </div>
      <div className="page-container section-pad" style={{ padding: '40px 24px' }}>
        <div className="grid-3" style={{ gap:16 }}>
          {provinces.map(p=>{
            const count = provStats[p.name]||0;
            return (
              <Link key={p.id} to={`/csos?province_id=${p.id}`}
                className="card card-hover" style={{ padding:24, textDecoration:'none', display:'block', borderLeft:'4px solid var(--green)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--gray-800)', marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
                      <MapPin size={18} style={{ color:'var(--green)' }} /> {p.name}
                    </div>
                    <div style={{ fontSize:'0.78rem', color:'var(--gray-400)' }}>Code: {p.code||'—'}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.6rem', fontWeight:800, color:'var(--green)', lineHeight:1 }}>{count}</div>
                    <div style={{ fontSize:'0.7rem', color:'var(--gray-400)' }}>CSOs</div>
                  </div>
                </div>
                <div style={{ marginTop:14, height:5, background:'var(--gray-100)', borderRadius:3 }}>
                  <div style={{ height:'100%', background:'var(--green)', borderRadius:3, width:`${Math.min((count/20)*100,100)}%`, transition:'width 1s ease' }}/>
                </div>
                <div style={{ marginTop:10, fontSize:'0.78rem', color:'var(--green)', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                  View CSOs <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Default export
export default CSOsPage;